import { Box, Card, Divider, GridItem, Image, RadioButton, Typography } from "@drone-js/core";
import type * as Types from "@drone-js/core/src/types/Types";
import type { ComponentProps, FunctionComponent, PropsWithChildren, ReactNode } from "react";
import type { ImportOptions } from "./constants";
import { useOnboardingTypeState } from "./onboardingTypeAtom";

interface Props extends Pick<ComponentProps<typeof Card>, "status" | "jacket">, PropsWithChildren {
    id: ImportOptions;
    title: string;
    subtitle?: string;
    logo: string[];
    iconSize?: string;
    mobileIconSize?: string;
    bottom?: ReactNode;
    flexDirection?: Types.FlexDirection;
    hasMobileLeftPadding?: boolean;
}

export const CardSelector: FunctionComponent<Props> = ({
    id,
    children,
    title,
    subtitle,
    flexDirection,
    logo,
    bottom,
    jacket,
    iconSize = "64px",
    mobileIconSize = "48px",
    hasMobileLeftPadding = false,
    ...rest
}) => {
    const [onboardingType, setOnboardingType] = useOnboardingTypeState();

    const handleClick = () => {
        setOnboardingType(id);
    };
    const selected = onboardingType === id;

    return (
        <GridItem sm={12}>
            <Card
                variant="outlined"
                type="interactive"
                status={selected ? "selected" : "default"}
                jacket={jacket}
                onClick={handleClick}
                {...rest}
            >
                <Box p="16">
                    <Box
                        display="flex"
                        flexDirection={flexDirection ?? "column-reverse"}
                        alignItems="center"
                        qM={{
                            flexDirection: "row",
                        }}
                    >
                        <Box
                            width="100%"
                            pt={flexDirection == "row" || flexDirection == "row-reverse" ? "0" : "24"}
                            qM={{
                                pt: "0",
                            }}
                        >
                            <RadioButton selection={selected ? "selected" : "unselected"}>
                                <Box
                                    ms="0"
                                    qM={{
                                        ms: "4",
                                    }}
                                >
                                    <Typography variant="heading-default" mt="-4">
                                        {title}
                                    </Typography>
                                    {subtitle?.trim() && (
                                        <Typography variant="body-default" textColor="base.subtle" mt="4">
                                            {subtitle}
                                        </Typography>
                                    )}
                                </Box>
                            </RadioButton>
                            <Box
                                ps={hasMobileLeftPadding ? "32" : "0"}
                                qM={
                                    !hasMobileLeftPadding && {
                                        ps: "32",
                                    }
                                }
                            >
                                {children}
                            </Box>
                        </Box>
                        <Box flexShrink={0} p="16" display="flex" gap="12">
                            {logo.map((src) => (
                                <Image
                                    width={mobileIconSize}
                                    height={mobileIconSize}
                                    objectFit="cover"
                                    loading="immediate"
                                    src={src}
                                    key={src}
                                    alt="logo"
                                    qM={{
                                        width: iconSize,
                                        height: iconSize,
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                    {bottom && (
                        <>
                            <Divider my="16" />
                            <Box
                                qM={{
                                    ps: "32",
                                }}
                            >
                                {bottom}
                            </Box>
                        </>
                    )}
                </Box>
            </Card>
        </GridItem>
    );
};
